"use client"

import Image from "next/image"
import { PointerEvent as ReactPointerEvent, useRef, useState } from "react"

const EMBLEM_STATES = [
  { label: "01 Front", rotation: -22 },
  { label: "02 Tide", rotation: 0 },
  { label: "03 Pearl", rotation: 22 },
]

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value))

const AboutEmblem = () => {
  const [rotation, setRotation] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const pointerStartX = useRef<number | null>(null)
  const rotationAtPointerStart = useRef(0)

  const updateFromPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    const panel = event.currentTarget.getBoundingClientRect()
    const normalized =
      (event.clientX - panel.left - panel.width / 2) / (panel.width / 2)
    setRotation(clamp(normalized * 22, -22, 22))
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerStartX.current !== null) {
      const delta = event.clientX - pointerStartX.current
      setRotation(clamp(rotationAtPointerStart.current + delta * 0.32, -72, 72))
      return
    }

    updateFromPointer(event)
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartX.current = event.clientX
    rotationAtPointerStart.current = rotation
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const finishPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    pointerStartX.current = null
    setIsDragging(false)
  }

  const handlePointerLeave = () => {
    if (!isDragging) setRotation(0)
  }

  return (
    <div className="sahil-reference-about__emblem">
      <div
        className={`sahil-reference-about__emblem-panel ${isDragging ? "is-dragging" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onPointerLeave={handlePointerLeave}
        role="group"
        aria-label="Turn the Sahil shell, pearl and wave emblem"
      >
        <div
          className="sahil-reference-about__emblem-ornament"
          aria-hidden="true"
        />
        <Image
          src="/assets/sahil/sahil-shell-sculpture.png"
          alt="Sahil shell, pearl and wave emblem"
          width={560}
          height={560}
          sizes="(max-width: 768px) 84vw, 420px"
          className="sahil-reference-about__emblem-art"
          style={{ transform: `rotateY(${rotation}deg)` }}
          draggable={false}
        />
        <div
          className="sahil-reference-about__emblem-states"
          role="group"
          aria-label="Emblem views"
        >
          {EMBLEM_STATES.map((state) => (
            <button
              key={state.label}
              type="button"
              aria-pressed={Math.abs(rotation - state.rotation) < 4}
              onClick={() => setRotation(state.rotation)}
            >
              {state.label}
            </button>
          ))}
        </div>
        <span className="sahil-reference-about__emblem-hint" aria-hidden="true">
          Move to turn
        </span>
      </div>
    </div>
  )
}

export default AboutEmblem
